// olap-client/src/components/MessageContextMenu.jsx
import React, { useContext, useEffect, useRef, useState, useLayoutEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Context } from '..';

const MessageContextMenu = observer(() => {
  const { chat, user } = useContext(Context);
  const menuRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState(false);

  // Реакции
  const reactions = ['👍', '❤️', '😂', '😮', '😢', '🔥', '🎉', ''];

  // Инициализируем позицию и видимость
  useEffect(() => {
    if (chat.contextMenu) {
      setPosition({ x: chat.contextMenu.x, y: chat.contextMenu.y });
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [chat.contextMenu]);

  // Корректировка позиции ПОСЛЕ рендера
  useLayoutEffect(() => {
    if (chat.contextMenu && menuRef.current) {
      const menu = menuRef.current;
      const menuRect = menu.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const padding = 10;

      let newX = chat.contextMenu.x;
      let newY = chat.contextMenu.y;

      // Если выходит за правую границу
      if (newX + menuRect.width > viewportWidth - padding) {
        newX = Math.max(padding, viewportWidth - menuRect.width - padding);
      }

      // Если выходит за нижнюю границу
      if (newY + menuRect.height > viewportHeight - padding) {
        newY = Math.max(padding, viewportHeight - menuRect.height - padding);
      }

      // Если выходит за левую границу
      if (newX < padding) {
        newX = padding;
      }

      // Если выходит за верхнюю границу
      if (newY < padding) {
        newY = padding;
      }

      setPosition({ x: newX, y: newY });
    }
  }, [chat.contextMenu, isVisible]);

  // Закрытие при клике вне меню, скролле и Escape
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        chat.hideContextMenu();
      }
    };

    const handleScroll = () => {
      chat.hideContextMenu();
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        chat.hideContextMenu();
      }
    };

    if (chat.contextMenu) {
      // Небольшая задержка, чтобы клик на открытии не закрыл сразу
      const timer = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('scroll', handleScroll, true);
        document.addEventListener('keydown', handleEscape);
      }, 10);
      
      return () => {
        clearTimeout(timer);
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('scroll', handleScroll, true);
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [chat.contextMenu]);

  if (!chat.contextMenu) return null;

  const { message } = chat.contextMenu;
  const isMine = message.senderId && String(message.senderId) === String(user.user.id);
  const isSystem = message.type === 'system';
  const isDeleted = message.isDeleted;

  return (
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        top: `${position.y}px`,
        left: `${position.x}px`,
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
        padding: '8px',
        zIndex: 10000,
        minWidth: '200px',
        maxWidth: '300px',
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 0.1s ease-in'
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* === РЕАКЦИИ (всегда сверху) === */}
      <div style={{
        display: 'flex',
        gap: '4px',
        marginBottom: '8px',
        paddingBottom: '8px',
        borderBottom: '1px solid #f0f0f0',
        flexWrap: 'wrap'
      }}>
        {reactions.map(emoji => {
          const hasReaction = message.reactions?.some(
            r => r.userId === user.user.id && r.emoji === emoji
          );
          
          return (
            <button
              key={emoji}
              onClick={() => {
                chat.toggleReaction(message.id, emoji);
                chat.hideContextMenu();
              }}
              style={{
                background: hasReaction ? '#e3f2fd' : 'transparent',
                border: hasReaction ? '1px solid #3390ec' : '1px solid #e4e4e5',
                borderRadius: '8px',
                padding: '4px 8px',
                cursor: 'pointer',
                fontSize: '18px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (!hasReaction) e.currentTarget.style.transform = 'scale(1.2)';
              }}
              onMouseLeave={(e) => {
                if (!hasReaction) e.currentTarget.style.transform = 'scale(1)';
              }}
              title={`Добавить ${emoji}`}
            >
              {emoji}
            </button>
          );
        })}
      </div>

      {/* Для системных и удалённых сообщений */}
      {isSystem || isDeleted ? (
        <MenuItem
          icon="📋"
          text="Копировать текст"
          onClick={() => {
            navigator.clipboard.writeText(message.text);
            chat.hideContextMenu();
          }}
        />
      ) : (
        <>
          <MenuItem
            icon="↩️"
            text="Ответить"
            onClick={() => {
              chat.setReplyingTo(message);
              chat.hideContextMenu();
            }}
          />

          <MenuItem
            icon="↪️"
            text="Переслать"
            onClick={() => {
              chat.setForwardMessage(message.id);
              chat.hideContextMenu();
            }}
          />

          <MenuItem
            icon="📋"
            text="Копировать текст"
            onClick={() => {
              navigator.clipboard.writeText(message.text);
              chat.hideContextMenu();
            }}
          />

          {isMine && <div style={{ height: '1px', background: '#f0f0f0', margin: '4px 0' }} />}

          {isMine && message.id && !message.id.startsWith('temp_') && (
            <MenuItem
              icon="✏️"
              text="Редактировать"
              onClick={() => {
                chat.startEditing(message);
                chat.hideContextMenu();
              }}
            />
          )}

          {isMine && message.id && !message.id.startsWith('temp_') && (
            <MenuItem
              icon="🗑️"
              text="Удалить"
              onClick={() => {
                if (window.confirm('Удалить сообщение?')) {
                  chat.deleteMessage(message.id);
                }
                chat.hideContextMenu();
              }}
              danger
            />
          )}
        </>
      )}
    </div>
  );
});

const MenuItem = ({ icon, text, onClick, danger }) => (
  <div
    onClick={onClick}
    style={{
      padding: '8px 12px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      fontSize: '14px',
      color: danger ? '#dc3545' : '#333',
      borderRadius: '6px',
      transition: 'background 0.2s'
    }}
    onMouseEnter={(e) => e.currentTarget.style.background = '#f4f4f5'}
    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
  >
    <span style={{ fontSize: '16px' }}>{icon}</span>
    <span>{text}</span>
  </div>
);

export default MessageContextMenu;